// Tiny i18n layer for the beta page. Two text layers get localized:
//   1. the page CHROME (button labels, step titles) — the STRINGS table here
//   2. the per-app COPY (headline/subtitle/body/steps/badges) — carried in
//      the `beta` block under `beta.i18n.<lang>`, picked by pickBetaCopy()
//
// Language is OPT-IN, English is the deterministic default (the global Play
// audience must never get auto-flipped): ?lang=cs in the URL wins; absent
// that we honour a cs-* browser locale; otherwise English. This is why a
// query param, not a separate slug or hard auto-detect — see the beta README.

import type { BetaCopy } from "./types";

export type Lang = "en" | "cs";

const SUPPORTED: Lang[] = ["en", "cs"];

export function resolveLang(): Lang {
  if (typeof window !== "undefined") {
    const q = new URLSearchParams(window.location.search).get("lang");
    if (q && (SUPPORTED as string[]).includes(q)) return q as Lang;
  }
  if (typeof navigator !== "undefined" && /^cs\b/i.test(navigator.language || "")) {
    return "cs";
  }
  return "en";
}

// pickBetaCopy — overlay the locale-specific copy (if present) on the
// English base. Missing fields fall back to the base so a partial
// translation never blanks a section.
export function pickBetaCopy(
  beta: BetaCopy | undefined,
  lang: Lang,
): BetaCopy | undefined {
  if (!beta || lang === "en") return beta;
  const loc = beta.i18n?.[lang];
  if (!loc) return beta;
  return {
    headline: loc.headline ?? beta.headline,
    subtitle: loc.subtitle ?? beta.subtitle,
    body: loc.body ?? beta.body,
    ask_questions: loc.ask_questions ?? beta.ask_questions,
    steps: loc.steps ?? beta.steps,
    badges: loc.badges ?? beta.badges,
  };
}

type Dict = Record<string, string>;

const STRINGS: Record<Lang, Dict> = {
  en: {
    inapp_warn_title: "Open this page in your browser",
    inapp_warn_intro:
      "The Facebook browser blocks the Google sign-in you need to join the test and install the app. Two taps to fix it:",
    inapp_warn_step1: "Tap the ⋮ menu in the top corner",
    inapp_warn_step2_android: "Choose “Open in Chrome”",
    inapp_warn_step2_ios: "Choose “Open in Safari”",
    inapp_warn_or: "Or copy the link and paste it into your browser:",
    inapp_copy_btn: "Copy the link",
    inapp_copied: "Copied — now paste it in your browser",
    loading: "Loading…",
    not_found_title: "App not found",
    not_found_body: "We don't have a beta page for {slug}.",
    hero_default: "Thanks for giving this a try!",
    paused_prefix: "Closed beta is paused right now — check back later or",
    paused_email: "email me",
    paused_suffix: "for an invite.",
    step_join_title: "Join the testers group",
    step_install_title: "Install the app",
    step_redeem_title: "Thanks for joining!",
    step_of: "Step {n} of 3",
    back: "← Back",
    join_intro: "Open the testers group, then follow the screenshot below.",
    join_open: "Open the testers group →",
    join_img_alt:
      "Screenshot of the testers group page with a 'Join this group' callout",
    join_done: "I've joined — next step →",
    install_intro:
      "Sign in to your phone with the same Google account you used to join the group, then tap the button below to install from the Play Store. It can take a few minutes for tester access to propagate — refresh the Play listing if it says \"not available\" the first time.",
    install_open_play: "Open Play Store →",
    install_play_pending: "Play Store link pending — check back soon.",
    install_done: "I've installed it — show my unlock code",
    install_done_onetime: "I've installed it →",
    install_qr_hint: "On a computer? Scan to open it on your phone.",
    install_back: "← Back to join the group",
    redeem_getting: "Getting your code…",
    redeem_no_codes:
      "All codes have been claimed for now. Check back later — more may become available.",
    redeem_error_retry: "Something went wrong — get my code",
    redeem_get: "Get my code",
    done_onetime_title: "You're all set!",
    done_onetime_body:
      "That's it — thanks for testing! Open the app and enjoy.",
    share_prompt: "Know another parent who'd love this? Share it:",
    share_btn: "Share the app",
    share_copied: "Link copied!",
    share_qr_hint: "Scan to open this page on your phone.",
    unlock_btn: "Unlock all premium features →",
    scan_hint: "Scan with your phone to unlock all premium features in the app.",
    feedback_btn: "Send me your feedback →",
    feedback_subject: "Beta feedback",
    access_subject: "Beta access request",
    how_it_works: "How it works",
    see_in_action: "See it in action",
    footer_by: "by",
  },
  cs: {
    inapp_warn_title: "Otevřete tuto stránku v prohlížeči",
    inapp_warn_intro:
      "Facebookový prohlížeč blokuje přihlášení Googlem, které potřebujete k zapojení do testu a instalaci aplikace. Stačí dvě klepnutí:",
    inapp_warn_step1: "Klepněte na nabídku ⋮ v horním rohu",
    inapp_warn_step2_android: "Zvolte „Otevřít v Chrome“",
    inapp_warn_step2_ios: "Zvolte „Otevřít v Safari“",
    inapp_warn_or: "Nebo zkopírujte odkaz a vložte ho do prohlížeče:",
    inapp_copy_btn: "Zkopírovat odkaz",
    inapp_copied: "Zkopírováno — vložte do prohlížeče",
    loading: "Načítání…",
    not_found_title: "Aplikace nenalezena",
    not_found_body: "Pro {slug} bohužel nemáme beta stránku.",
    hero_default: "Díky, že to zkoušíte!",
    paused_prefix: "Uzavřená beta je teď pozastavená — zkuste to později nebo",
    paused_email: "mi napište",
    paused_suffix: "a pošlu vám pozvánku.",
    step_join_title: "Připojte se do skupiny testerů",
    step_install_title: "Nainstalujte aplikaci",
    step_redeem_title: "Díky, že jste se přidali!",
    step_of: "Krok {n} ze 3",
    back: "← Zpět",
    join_intro: "Otevřete skupinu testerů a pak postupujte podle snímku níže.",
    join_open: "Otevřít skupinu testerů →",
    join_img_alt:
      "Snímek stránky skupiny testerů s tlačítkem „Join this group“",
    join_done: "Přidal/a jsem se — další krok →",
    install_intro:
      "Přihlaste se na telefonu stejným účtem Google, kterým jste se přidali do skupiny, a pak klepnutím na tlačítko níže nainstalujte aplikaci z Obchodu Play. Přístup pro testery se může pár minut načítat — když to napoprvé hlásí „není dostupné“, obnovte stránku v Obchodu Play.",
    install_open_play: "Otevřít Obchod Play →",
    install_play_pending: "Odkaz na Obchod Play se připravuje — zkuste to brzy znovu.",
    install_done: "Nainstalováno — zobrazit můj odemykací kód",
    install_done_onetime: "Nainstalováno →",
    install_qr_hint: "Jste na počítači? Naskenujte a otevřete v telefonu.",
    install_back: "← Zpět k připojení do skupiny",
    redeem_getting: "Získávám váš kód…",
    redeem_no_codes:
      "Všechny kódy jsou prozatím rozebrané. Zkuste to později — možná přibydou další.",
    redeem_error_retry: "Něco se pokazilo — získat můj kód",
    redeem_get: "Získat můj kód",
    done_onetime_title: "Hotovo, jste uvnitř!",
    done_onetime_body:
      "A je to — díky, že testujete! Otevřete aplikaci a užijte si ji.",
    share_prompt: "Znáte dalšího rodiče, kterého by to potěšilo? Sdílejte:",
    share_btn: "Sdílet aplikaci",
    share_copied: "Odkaz zkopírován!",
    share_qr_hint: "Naskenujte a otevřete tuto stránku v telefonu.",
    unlock_btn: "Odemknout všechny prémiové funkce →",
    scan_hint: "Naskenujte telefonem a odemkněte v aplikaci všechny prémiové funkce.",
    feedback_btn: "Pošlete mi zpětnou vazbu →",
    feedback_subject: "Zpětná vazba k beta verzi",
    access_subject: "Žádost o přístup k beta verzi",
    how_it_works: "Jak to funguje",
    see_in_action: "Podívejte se na to",
    footer_by: "od",
  },
};

export function t(
  lang: Lang,
  key: string,
  vars?: Record<string, string | number>,
): string {
  let s = STRINGS[lang][key] ?? STRINGS.en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replace(`{${k}}`, String(v));
    }
  }
  return s;
}
